
import React from 'react';
import { Search, Filter, Calendar, CalendarDays, Layers, Zap, Clock, CalendarX, Info, RefreshCw, Presentation } from 'lucide-react';
import { getPerspectiveIcon } from '../utils/formatters';

const FilterSection = ({
  searchTerm,
  setSearchTerm,
  selectedPerspective,
  setSelectedPerspective,
  perspectives,
  startDate,
  setStartDate,
  endDate,
  showOnlyUnseen,
  setShowOnlyUnseen,
  setEndDate,
  getDateRange,
  setQuickPeriod,
  clearAllFilters,
  filteredData,
  indicators,
  onExportPptx,
  isGeneratingPptx
}) => {
  const dateRange = getDateRange();

  const PerspectiveIcon = ({ perspective }) => {
    const iconName = getPerspectiveIcon(perspective);
    if (iconName === 'dollar-sign') return <span>💰</span>;
    if (iconName === 'users') return <span>👥</span>;
    return <span>📊</span>;
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50/50 rounded-2xl p-5 mb-5 shadow-lg border border-gray-100 backdrop-blur-sm">
      <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200">
        <h3 className="text-lg font-bold bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent flex items-center gap-2.5">
          <div className="p-1.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow">
            <Search className="w-4 h-4 text-white" />
          </div>
          Filtros e Busca Avançada
        </h3>

        {onExportPptx && (
          <button
            onClick={onExportPptx}
            disabled={isGeneratingPptx}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium shadow transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(90deg,#7a2433,#5e1a26)' }}
            title="Exportar um slide por indicador do período (PPTX)"
          >
            {isGeneratingPptx ? (
              <>
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                Gerando PPTX...
              </>
            ) : (
              <>
                <Presentation className="w-4 h-4" />
                Exportar PPTX
              </>
            )}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div className="group">
          <label className="flex items-center gap-2 text-xs font-bold text-gray-700 mb-1.5">
            <div className="p-1 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg group-hover:scale-110 transition-transform duration-200">
              <Search className="w-4 h-4 text-blue-600" />
            </div>
            Busca Inteligente
          </label>
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
            <input
              type="text"
              placeholder="Buscar por indicador, notas, análises..."
              className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-300 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
          </div>
        </div>

        <div className="group">
          <label className="flex items-center gap-2 text-xs font-bold text-gray-700 mb-1.5">
            <div className="p-1 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg group-hover:scale-110 transition-transform duration-200">
              <Layers className="w-4 h-4 text-purple-600" />
            </div>
            Perspectiva BSC
          </label>
          <div className="relative group">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-purple-500 transition-colors duration-200" />
            <select
              className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-300 appearance-none bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-lg cursor-pointer"
              value={selectedPerspective}
              onChange={(e) => setSelectedPerspective(e.target.value)}
            >
              <option value="All">📊 Todas as Perspectivas</option>
              {perspectives.map(p => (
                <option key={p} value={p}>
                  {p === 'Financial Reports' ? '💰 ' + p : '👥 ' + p}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent border-t-gray-400 group-focus-within:border-t-purple-500 transition-colors duration-200"></div>
            </div>
          </div>
        </div>

        <div className="group">
          <label className="flex items-center gap-2 text-xs font-bold text-gray-700 mb-1.5">
            <div className="p-1 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg group-hover:scale-110 transition-transform duration-200">
              <Calendar className="w-4 h-4 text-green-600" />
            </div>
            Data Inicial
          </label>
          <div className="relative group">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-green-500 transition-colors duration-200" />
            <input
              type="date"
              className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-lg"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={dateRange.min}
              max={dateRange.max}
            />
          </div>
        </div>

        <div className="group">
          <label className="flex items-center gap-2 text-xs font-bold text-gray-700 mb-1.5">
            <div className="p-1 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg group-hover:scale-110 transition-transform duration-200">
              <CalendarDays className="w-4 h-4 text-orange-600" />
            </div>
            Data Final
          </label>
          <div className="relative group">
            <CalendarDays className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-orange-500 transition-colors duration-200" />
            <input
              type="date"
              className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-all duration-300 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-lg"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={dateRange.min}
              max={dateRange.max}
            />
          </div>
        </div>
      </div>

      {/* Quick Filters - Redesenhados */}
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-blue-50/80 to-indigo-50/80 rounded-xl p-4 border border-blue-100/50">
          <label className="flex items-center gap-2.5 text-xs font-bold text-gray-700 mb-3">
            <div className="p-1.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-md">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm">Filtros Rápidos - Perspectivas</span>
          </label>
          <div className="flex flex-wrap gap-3">
            {perspectives.map(p => (
              <button
                key={p}
                className={`group flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 border shadow-sm hover:shadow-md hover:-translate-y-0.5 ${selectedPerspective === p
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-blue-600 shadow-xl scale-105'
                  : 'bg-white/80 text-gray-700 border-gray-200 hover:bg-white hover:border-blue-300'
                  }`}
                onClick={() => setSelectedPerspective(p)}
              >
                <PerspectiveIcon perspective={p} />
                {p}
                {selectedPerspective === p && <div className="w-2 h-2 bg-white rounded-full animate-pulse ml-1"></div>}
              </button>
            ))}
            <button
              className={`group flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 border shadow-sm hover:shadow-md hover:-translate-y-0.5 ${selectedPerspective === 'All'
                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-blue-600 shadow-xl scale-105'
                : 'bg-white/80 text-gray-700 border-gray-200 hover:bg-white hover:border-blue-300'
                }`}
              onClick={() => setSelectedPerspective('All')}
            >
              🌐 Todas
              {selectedPerspective === 'All' && <div className="w-2 h-2 bg-white rounded-full animate-pulse ml-1"></div>}
            </button>
            <div className="flex items-center gap-3 mt-4">
              <input
                id="only-unseen"
                type="checkbox"
                checked={showOnlyUnseen}
                onChange={(e) => setShowOnlyUnseen(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="only-unseen" className="text-sm font-bold text-gray-700">
                Mostrar somente não vistos
              </label>
            </div>
          </div>
        </div>


      </div>


    </div>
  );
};

export default FilterSection;
